import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-article-page',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        CardModule,
        ToastModule,
        ConfirmDialogModule,
        TooltipModule,
        BadgeModule
    ],
    providers: [MessageService, ConfirmationService],
    template: `
        <div class="min-h-screen bg-gray-50 p-4">
            <div class="mx-auto max-w-7xl">
                <!-- Header -->
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div class="text-center">
                        <h1 class="text-3xl font-bold text-gray-900 mb-2">Artikel Medis</h1>
                        <p class="text-gray-600">Kelola konten artikel untuk pengguna platform</p>
                    </div>
                </div>

                <!-- Main Actions -->
                <div class="max-w-xl mx-auto mb-8">
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                        <div class="text-center">
                            <i class="pi pi-list text-4xl text-blue-600 mb-4"></i>
                            <h3 class="text-xl font-semibold text-gray-900 mb-2">Daftar Artikel Medis</h3>
                            <p class="text-gray-600 mb-4">Baca seluruh panduan, tips OSCE, dan artikel medis dari Mediko</p>
                            <p-button
                                (onClick)="goToList()"
                                label="Buka Semua Artikel"
                                severity="info"
                                size="large"
                                class="w-full">
                            </p-button>
                        </div>
                    </div>
                </div>

                <!-- Statistics -->
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-5 flex items-center gap-2">
                        <i class="pi pi-chart-bar text-blue-600"></i>
                        Statistik Artikel
                    </h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Total Articles -->
                        <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200 hover:shadow-md transition-all">
                            <div class="flex items-center justify-between mb-3">
                                <div class="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                                    <i class="pi pi-file text-white text-xl"></i>
                                </div>
                            </div>
                            <div class="text-3xl font-bold text-blue-900 mb-1">{{ totalArticles }}</div>
                            <div class="text-sm font-medium text-blue-700">Total Artikel</div>
                        </div>

                        <!-- Recent Articles Info -->
                        <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200 hover:shadow-md transition-all">
                            <div class="flex items-center justify-between mb-3">
                                <div class="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                                    <i class="pi pi-clock text-white text-xl"></i>
                                </div>
                            </div>
                            <div class="text-3xl font-bold text-green-900 mb-1">{{ recentArticles.length }}</div>
                            <div class="text-sm font-medium text-green-700">Artikel Terbaru di Dashboard</div>
                        </div>
                    </div>
                </div>

                <!-- Recent Articles -->
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Artikel Terbaru</h3>
                    <div *ngIf="recentArticles.length > 0" class="space-y-3">
                        <div *ngFor="let article of recentArticles"
                             class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                            <div class="flex items-center space-x-3 cursor-pointer" (click)="viewArticle(article)">
                                <i class="pi pi-file text-blue-600 text-2xl"></i>
                                <div>
                                    <div class="font-medium text-gray-900">{{ article.judul }}</div>
                                    <div class="text-sm text-gray-500">{{ article.kategori }} • {{ formatDate(article.tanggal) }}</div>
                                </div>
                            </div>
                            <div *ngIf="isAuthorizedUser" class="flex items-center space-x-2">
                                <p-button
                                    (onClick)="editArticle(article)"
                                    severity="info"
                                    icon="pi pi-pencil"
                                    size="small"
                                    text="true"
                                    pTooltip="Edit Artikel">
                                </p-button>
                            </div>
                            <i *ngIf="!isAuthorizedUser" class="pi pi-chevron-right text-gray-400"></i>
                        </div>
                    </div>
                    <div *ngIf="recentArticles.length === 0" class="text-center py-8 text-gray-500">
                        <i class="pi pi-file text-4xl mb-4 block"></i>
                        <p>Belum ada artikel</p>
                    </div>
                </div>
            </div>
        </div>

        <p-confirmDialog></p-confirmDialog>
        <p-toast></p-toast>
    `
})
export class ArticlePage implements OnInit {
    totalArticles = 0;
    recentArticles: any[] = [];
    isAuthorizedUser = false;

    constructor(
        private router: Router,
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {}

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadDashboardData();
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY';
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.isAuthorizedUser = false;
        }
    }

    loadDashboardData() {
        this.userService.getArticles().then(
            (response) => {
                const articles = response.data || response || [];
                this.totalArticles = articles.length;
                this.recentArticles = articles.slice(0, 5); // Show 5 most recent
            },
            (error) => {
                console.error('Error loading articles:', error);
            }
        );
    }

    goToCreate() {
        this.router.navigate(['/app/article/create']);
    }

    goToList() {
        this.router.navigate(['/app/article/list']);
    }

    editArticle(article: any) {
        this.router.navigate(['/app/article/edit', article.id]);
    }

    viewArticle(article: any) {
        this.router.navigate(['/app/article/show', article.id]);
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }
}
